import React from "react";
import { Box, Grid, Paper, Stack, Typography } from "@mui/material";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import EventNoteIcon from "@mui/icons-material/EventNote";
import RoomServiceIcon from "@mui/icons-material/RoomService";
import LocalDiningIcon from "@mui/icons-material/LocalDining";
import FreeBreakfastIcon from "@mui/icons-material/FreeBreakfast";
import LunchDiningIcon from "@mui/icons-material/LunchDining";
import DinnerDiningIcon from "@mui/icons-material/DinnerDining";
import LocalCafeIcon from "@mui/icons-material/LocalCafe";

import { SectionTitle } from "./StyledComponents";

/**
 * Creates a signature for a day's meal plan based on recipe IDs and servings
 * @param {Object} dayData - The day's meal plan data
 * @returns {string} - A unique signature string for the day
 */
const createDaySignature = (dayData) => {
  if (!dayData || !dayData.mealTypes) return '';
  
  const mealTypeOrder = ['breakfast', 'lunch', 'dinner', 'snack'];
  const signatureParts = [];
  
  mealTypeOrder.forEach(mealType => {
    const mealRecipes = dayData.mealTypes[mealType] || [];
    if (mealRecipes.length > 0) {
      // Sort recipes by recipe ID to ensure consistent comparison
      const recipeIds = mealRecipes
        .map(recipe => {
          const recipeId = recipe.recipe?.id || recipe.recipeId || recipe.id || '';
          const servings = recipe.servingsPerDay || 1;
          return `${recipeId}:${servings}`;
        })
        .sort()
        .join(',');
      signatureParts.push(`${mealType}:${recipeIds}`);
    }
  });
  
  return signatureParts.join('|');
};

/**
 * Groups days by their meal plan signature to identify identical days
 * @param {Object} weeklySchedule - Weekly schedule of recipes by day
 * @returns {Object} - Map of day names to their group color index
 */
const groupDaysBySignature = (weeklySchedule) => {
  if (!weeklySchedule) return {};
  
  const daysOfWeek = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const signatureGroups = {};
  const dayGroups = {};
  
  // Group days by signature
  daysOfWeek.forEach(day => {
    const dayData = weeklySchedule[day];
    if (!dayData) return;
    
    const signature = createDaySignature(dayData);
    if (!signature) return; // Skip empty days
    
    if (!signatureGroups[signature]) {
      signatureGroups[signature] = [];
    }
    signatureGroups[signature].push(day);
  });
  
  // Assign group indices (only for groups with 2+ days)
  let groupIndex = 0;
  Object.keys(signatureGroups).forEach(signature => {
    const days = signatureGroups[signature];
    if (days.length > 1) {
      days.forEach(day => {
        dayGroups[day] = groupIndex;
      });
      groupIndex++;
    }
  });
  
  return dayGroups;
};

/**
 * MealPlanRecipes component displays all recipes included in the meal plan
 * Can display either as a simple list or organized by weekly schedule
 *
 * @param {Array} recipes - List of scheduled recipes in the meal plan
 * @param {Object} weeklySchedule - Weekly schedule of recipes by day (optional)
 * @param {Object} dailyNutritionTotals - Daily nutrition breakdowns (optional)
 * @param {Function} onRecipeClick - Callback when a recipe is clicked (optional)
 */
const MealPlanRecipes = ({ recipes, weeklySchedule, dailyNutritionTotals, onRecipeClick }) => {

  // Helper function to get meal type icon
  const getMealTypeIcon = (mealType) => {
    switch (mealType) {
      case 'breakfast':
        return <FreeBreakfastIcon fontSize="small" color="primary" />;
      case 'lunch':
        return <LunchDiningIcon fontSize="small" color="primary" />;
      case 'dinner':
        return <DinnerDiningIcon fontSize="small" color="primary" />;
      case 'snack':
        return <LocalCafeIcon fontSize="small" color="primary" />;
      default:
        return <LocalDiningIcon fontSize="small" color="primary" />;
    }
  };

  // Helper function to render a compact recipe card
  const renderRecipeCard = (recipe, key) => {
    const recipeName = recipe.recipe?.name || recipe.name;
    const calories = recipe.recipe?.kCals || recipe.kCals || 0;
    const mealType = recipe.recipe?.mealType || recipe.mealType || 'other';
    
    return (
      <Box
        key={key}
        onClick={() => onRecipeClick && onRecipeClick(recipe)}
        sx={{
          p: 1.5,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          backgroundColor: 'background.paper',
          transition: 'all 0.2s ease',
          cursor: onRecipeClick ? 'pointer' : 'default',
          '&:hover': {
            backgroundColor: 'action.hover',
            borderColor: 'primary.main',
            ...(onRecipeClick && {
              boxShadow: 2,
              transform: 'translateY(-2px)'
            })
          }
        }}
      >
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={12} sm={8}>
            <Stack direction="row" alignItems="center" spacing={1}>
              {getMealTypeIcon(mealType)}
              <Typography variant="subtitle2" fontWeight={600} sx={{ flexGrow: 1 }}>
                {recipeName}
              </Typography>
            </Stack>
          </Grid>
          
          <Grid item xs={6} sm={2}>
            <Stack direction="row" alignItems="center" spacing={0.5} justifyContent="center">
              <RoomServiceIcon fontSize="small" color="action" />
              <Typography variant="caption" color="text.secondary">
                {recipe.servingsPerDay || 1}
              </Typography>
            </Stack>
          </Grid>
          
          <Grid item xs={6} sm={2}>
            <Typography variant="caption" color="text.secondary" textAlign="center">
              {Math.round(calories)} cal
            </Typography>
          </Grid>
        </Grid>
      </Box>
    );
  };

  // Helper function to render weekly schedule in a compact grid format
  const renderWeeklySchedule = () => {
    const daysOfWeek = [
      'monday', 'tuesday', 'wednesday', 'thursday', 
      'friday', 'saturday', 'sunday'
    ];

    const mealTypeOrder = ['breakfast', 'lunch', 'dinner', 'snack'];
    const mealTypeLabels = {
      breakfast: 'Breakfast',
      lunch: 'Lunch', 
      dinner: 'Dinner',
      snack: 'Snacks'
    };

    // Group days by signature to get color assignments
    const dayGroups = groupDaysBySignature(weeklySchedule);
    
    // Color palette for day groups (subtle, low opacity)
    const groupColors = [
      { bg: 'rgba(25, 118, 210, 0.08)', border: 'rgba(25, 118, 210, 0.2)' }, // primary
      { bg: 'rgba(46, 125, 50, 0.08)', border: 'rgba(46, 125, 50, 0.2)' }, // success
      { bg: 'rgba(237, 108, 2, 0.08)', border: 'rgba(237, 108, 2, 0.2)' }, // warning
      { bg: 'rgba(156, 39, 176, 0.08)', border: 'rgba(156, 39, 176, 0.2)' }, // purple
    ];

    return (
      <Grid container spacing={2}>
        {daysOfWeek.map(day => {
          const dayData = weeklySchedule[day];
          if (!dayData || !dayData.mealTypes) return null;
          
          // Check if this day has any recipes
          const hasRecipes = mealTypeOrder.some(mealType => 
            dayData.mealTypes[mealType] && dayData.mealTypes[mealType].length > 0
          );
          
          if (!hasRecipes) return null;

          // Get color for this day if it's part of a group
          const groupIndex = dayGroups[day];
          const dayColor = groupIndex !== undefined 
            ? groupColors[groupIndex % groupColors.length]
            : null;

          return (
            <Grid item xs={12} key={day}>
              <Paper 
                variant="outlined" 
                sx={{ 
                  p: 2, 
                  borderRadius: 2,
                  backgroundColor: dayColor ? dayColor.bg : 'background.paper',
                  borderColor: dayColor ? dayColor.border : 'divider',
                  border: dayColor ? `2px solid ${dayColor.border}` : '1px solid',
                  transition: 'all 0.2s ease',
                }}
              >
                {/* Day Header */}
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  mb: 2,
                  pb: 1,
                  borderBottom: '1px solid',
                  borderColor: 'divider'
                }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <EventNoteIcon color="primary" />
                    <Typography variant="h6" sx={{ textTransform: 'capitalize', fontWeight: 600 }}>
                      {day}
                    </Typography>
                  </Stack>
                  
                  {/* Daily nutrition summary - more compact */}
                  {dailyNutritionTotals && dailyNutritionTotals[day] && (
                    <Box sx={{ 
                      display: 'flex',
                      gap: 2,
                      alignItems: 'center'
                    }}>
                      <Typography variant="caption" color="primary.main" fontWeight={600}>
                        Daily Total: {Math.round(dailyNutritionTotals[day].total.kCals)} cal
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        P: {Math.round(dailyNutritionTotals[day].total.proteins)}g | 
                        C: {Math.round(dailyNutritionTotals[day].total.carbs)}g | 
                        F: {Math.round(dailyNutritionTotals[day].total.fats)}g
                        {dailyNutritionTotals[day].total.fiber > 0
                          ? ` | Fi: ${Math.round(dailyNutritionTotals[day].total.fiber)}g`
                          : ""}
                      </Typography>
                    </Box>
                  )}
                </Box>
                
                {/* Meal Types in a grid layout */}
                <Grid container spacing={2}>
                  {mealTypeOrder.map(mealType => {
                    const mealRecipes = dayData.mealTypes[mealType] || [];
                    if (mealRecipes.length === 0) return null;
                    
                    return (
                      <Grid item xs={12} sm={6} md={3} key={`${day}-${mealType}`}>
                        <Box sx={{ 
                          border: '1px solid',
                          borderColor: 'grey.200',
                          borderRadius: 1,
                          p: 1.5,
                          height: '100%',
                          backgroundColor: 'grey.50'
                        }}>
                          {/* Meal Type Header */}
                          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                            {getMealTypeIcon(mealType)}
                            <Typography 
                              variant="subtitle2" 
                              fontWeight={600}
                              color="text.primary"
                            >
                              {mealTypeLabels[mealType]}
                            </Typography>
                            {mealRecipes.length > 1 && (
                              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                ({mealRecipes.length} options)
                              </Typography>
                            )}
                          </Stack>
                          
                          {/* Recipe List */}
                          <Stack spacing={1}>
                            {mealRecipes.map((recipe, index) => {
                              const recipeName = recipe.recipe?.name || recipe.name;
                              const calories = recipe.recipe?.kCals || recipe.kCals || 0;
                              
                              return (
                                <Box 
                                  key={`${day}_${mealType}_${index}`}
                                  onClick={() => onRecipeClick && onRecipeClick(recipe)}
                                  sx={{ 
                                    p: 1,
                                    backgroundColor: 'background.paper',
                                    borderRadius: 0.5,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    cursor: onRecipeClick ? 'pointer' : 'default',
                                    transition: 'all 0.2s ease',
                                    '&:hover': onRecipeClick ? {
                                      backgroundColor: 'action.hover',
                                      borderColor: 'primary.main',
                                      boxShadow: 1
                                    } : {}
                                  }}
                                >
                                  <Typography 
                                    variant="body2" 
                                    fontWeight={500} 
                                    sx={{ 
                                      mb: 0.5,
                                      ...(onRecipeClick && {
                                        color: 'primary.main',
                                        '&:hover': {
                                          textDecoration: 'underline'
                                        }
                                      })
                                    }}
                                  >
                                    {recipeName}
                                  </Typography>
                                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                      <RoomServiceIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                      <Typography variant="caption" color="text.secondary">
                                        {recipe.servingsPerDay || 1} serving{(recipe.servingsPerDay || 1) > 1 ? 's' : ''}
                                      </Typography>
                                    </Stack>
                                    <Typography variant="caption" color="text.secondary">
                                      {Math.round(calories)} cal
                                    </Typography>
                                  </Stack>
                                </Box>
                              );
                            })}
                          </Stack>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  if (!recipes || recipes.length === 0) {
    return (
      <Box sx={{ mb: 2 }}>
        <SectionTitle variant="h5">
          <MenuBookIcon />
          Recipes Included
        </SectionTitle>
        <Paper
          variant="outlined"
          sx={{
            borderRadius: 2,
            p: 2,
            mt: 1,
            textAlign: "center",
            backgroundColor: "background.paper",
          }}
        >
          <Typography variant="body1" color="text.secondary">
            No recipes have been added to this meal plan
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 2 }}>
      <SectionTitle variant="h5">
        <MenuBookIcon />
        Recipes Included
      </SectionTitle>

      {/* Display weekly schedule if available, otherwise show simple list */}
      {weeklySchedule ? (
        renderWeeklySchedule()
      ) : (
        // Simple list view (fallback) - also improved
        <Grid container spacing={1} sx={{ mt: 1 }}>
          {recipes
            .filter((recipe) => recipe && (recipe.name || recipe.recipe?.name))
            .map((recipe, index) => (
              <Grid item xs={12} sm={6} md={4} key={`recipe_${index}`}>
                {renderRecipeCard(recipe, `recipe_${index}`)}
              </Grid>
            ))}
        </Grid>
      )}
    </Box>
  );
};

export default MealPlanRecipes;
